/**
 * DataTable — virtualized, sortable, searchable, exportable table.
 *
 * Backed by `@tanstack/react-virtual` so even 100k rows scroll smoothly
 * (only ~20 DOM nodes at any time). Designed to be plugged into the
 * `useInfiniteList` hook for cursor-paginated data.
 *
 * Props:
 *   columns         — [{ key, header, render?, sortable?, width?, align? }]
 *   items           — current rows (virtualized)
 *   isLoading       — initial-load spinner
 *   isLoadingMore   — show "loading more…" pill at bottom
 *   hasMore         — controls scroll-to-load trigger
 *   onLoadMore      — fired when scrolled near bottom
 *   onRowClick      — optional, fired with row on click
 *   getRowKey       — function returning a stable key per row
 *   emptyState      — node shown when no rows
 *   error           — error string (banner above table)
 *   onSortChange    — (key, dir) — called when user clicks a sortable header
 *   csvExport       — { filename, columns? } (columns override for export)
 *   searchable      — show built-in search box (filters items locally)
 *   estRowHeight    — row height in px (default 44)
 *   maxHeight       — container max height in px (default 600)
 *   testid          — root data-testid prefix
 *
 * Visual conventions used app-wide (modern-classic, no AI-slop):
 *   - White background, soft neutral-200 borders
 *   - Pink-500 accent for sort + active selection
 *   - Sticky header with subtle shadow on scroll
 *   - Roboto Mono for numeric columns (align right)
 *   - Empty / loading / error states are first-class
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download, AlertCircle, Loader2 } from 'lucide-react';
import { getTodayLocal } from '../../lib/dateUtils';

const cellAlign = (align) =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

const toCsvValue = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const DataTable = ({
  columns,
  items,
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  onRowClick,
  getRowKey = (row, i) => row?._id || row?.id || i,
  emptyState,
  error,
  onSortChange,
  csvExport,
  searchable = false,
  estRowHeight = 44,
  maxHeight = 600,
  testid = 'data-table',
}) => {
  const parentRef = useRef(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ key: null, dir: null });

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((row) =>
      columns.some((c) => {
        const v = c.render ? null : row[c.key];
        const s = (v ?? '').toString().toLowerCase();
        return s.includes(q);
      })
    );
  }, [items, search, columns]);

  const virt = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estRowHeight,
    overscan: 8,
  });

  // Auto-fetch more when within 200px of the bottom.
  useEffect(() => {
    const el = parentRef.current;
    if (!el || !hasMore || isLoadingMore) return undefined;
    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        onLoadMore?.();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, isLoadingMore, onLoadMore]);

  const toggleSort = (key) => {
    let nextDir = 'asc';
    if (sortBy.key === key) {
      nextDir = sortBy.dir === 'asc' ? 'desc' : sortBy.dir === 'desc' ? null : 'asc';
    }
    setSortBy({ key: nextDir ? key : null, dir: nextDir });
    onSortChange?.(nextDir ? key : null, nextDir);
  };

  const doExport = () => {
    if (!csvExport) return;
    const cols = csvExport.columns || columns;
    const header = cols.map((c) => c.header || c.key);
    const lines = [header.map(toCsvValue).join(',')];
    filtered.forEach((row) => {
      lines.push(cols.map((c) => {
        if (c.exportValue) return toCsvValue(c.exportValue(row));
        if (c.render) return ''; // render-only cols can't be auto-exported
        return toCsvValue(row[c.key]);
      }).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${csvExport.filename || 'export'}_${getTodayLocal()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden" data-testid={testid}>
      {/* Toolbar */}
      {(searchable || csvExport) && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-neutral-100 bg-neutral-50/40">
          <div className="flex items-center gap-2 flex-1">
            {searchable && (
              <div className="relative max-w-sm flex-1">
                <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-pink-300"
                  data-testid={`${testid}-search`}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-neutral-500">
            <span data-testid={`${testid}-count`}>{filtered.length} {hasMore ? '+' : ''}rows</span>
            {csvExport && (
              <button
                onClick={doExport}
                disabled={filtered.length === 0}
                data-testid={`${testid}-export`}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-neutral-200 rounded-md hover:bg-white disabled:opacity-50"
              >
                <Download className="w-3 h-3" />
                <span>CSV</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-xs text-red-700 inline-flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center bg-neutral-50 border-b border-neutral-100 sticky top-0 z-10">
        {columns.map((c) => {
          const isSorted = sortBy.key === c.key;
          return (
            <div
              key={c.key}
              role={c.sortable ? 'button' : undefined}
              onClick={c.sortable ? () => toggleSort(c.key) : undefined}
              data-testid={`${testid}-col-${c.key}`}
              className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-neutral-500 ${cellAlign(c.align)} ${c.sortable ? 'cursor-pointer select-none hover:text-neutral-700' : ''}`}
              style={{ flex: c.width ? `0 0 ${c.width}px` : 1, minWidth: c.width || 0 }}
            >
              <span className="inline-flex items-center gap-1">
                {c.header}
                {c.sortable && !isSorted && <ArrowUpDown className="w-3 h-3 opacity-40" />}
                {isSorted && sortBy.dir === 'asc' && <ArrowUp className="w-3 h-3 text-pink-600" />}
                {isSorted && sortBy.dir === 'desc' && <ArrowDown className="w-3 h-3 text-pink-600" />}
              </span>
            </div>
          );
        })}
      </div>

      {/* Body (virtualized) */}
      <div
        ref={parentRef}
        className="overflow-auto"
        style={{ maxHeight }}
      >
        {isLoading && filtered.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-neutral-500 inline-flex items-center justify-center w-full gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-neutral-500" data-testid={`${testid}-empty`}>
            {emptyState || (search ? `No rows match "${search}"` : 'No data')}
          </div>
        ) : (
          <div style={{ height: virt.getTotalSize(), position: 'relative' }}>
            {virt.getVirtualItems().map((vRow) => {
              const row = filtered[vRow.index];
              return (
                <div
                  key={getRowKey(row, vRow.index)}
                  data-testid={`${testid}-row-${vRow.index}`}
                  onClick={() => onRowClick?.(row)}
                  className={`flex items-center border-b border-neutral-100 text-sm
                    ${onRowClick ? 'cursor-pointer hover:bg-neutral-50' : ''}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: `translateY(${vRow.start}px)`,
                    height: vRow.size,
                  }}
                >
                  {columns.map((c) => (
                    <div
                      key={c.key}
                      className={`px-3 py-2 truncate ${cellAlign(c.align)} ${c.align === 'right' ? 'tabular-nums' : ''} text-neutral-800`}
                      style={{ flex: c.width ? `0 0 ${c.width}px` : 1, minWidth: c.width || 0 }}
                    >
                      {c.render ? c.render(row) : row[c.key]}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {isLoadingMore && (
          <div className="px-3 py-3 text-center text-xs text-neutral-500 inline-flex items-center justify-center w-full gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading more…
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
